import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DollarSign, Info, ExternalLink, ShieldCheck, Loader2 } from "lucide-react";

export default function Earnings() {
  const [data, setData] = useState(null);
  const [connect, setConnect] = useState(null);
  const [starting, setStarting] = useState(false);
  const [params] = useSearchParams();

  const refresh = useCallback(() => {
    api.get("/me/earnings").then(({ data }) => setData(data));
    api.get("/payments/helper/status").then(({ data }) => setConnect(data)).catch(() => setConnect({ connected: false }));
  }, []);

  useEffect(() => {
    refresh();
    if (params.get("connect") === "return") toast.success("Welcome back — checking your Stripe status…");
  }, [refresh, params]);

  const startConnect = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/payments/helper/connect", { origin_url: window.location.origin });
      window.location.href = data.onboarding_url;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not start Stripe onboarding";
      // 503 = platform Connect not enabled — show a longer, clearer toast
      if (e?.response?.status === 503) {
        toast.error(detail, { duration: 10000 });
      } else {
        toast.error(detail);
      }
      setStarting(false);
    }
  };

  if (!data) return <div className="min-h-screen"><Navbar /><div className="p-10 text-center text-muted-foreground">Loading…</div></div>;

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-xs font-mono-label text-[color:hsl(var(--amber))]">Private · you only</div>
        <h1 className="mt-2 font-display font-extrabold text-3xl">Your CLING earnings</h1>
        <p className="mt-3 text-muted-foreground">Kudos and Immediate Help payouts land in your Stripe balance and are paid to your bank on Stripe's rolling schedule.</p>

        <ConnectPayoutsCard connect={connect} onStart={startConnect} starting={starting} onRefresh={refresh} />

        <div className="mt-6 grid sm:grid-cols-3 gap-3">
          <Stat label="Free Kudos" value={data.kudos_free_count} />
          <Stat label="Monetary Kudos" value={data.kudos_monetary_count} />
          <Stat label="Plan" value={data.plan?.toUpperCase() || "BASIC"} accent />
        </div>

        <div className="mt-6 border rounded-2xl bg-card p-5" data-testid="earnings-panel">
          <div className="flex items-center gap-2 text-sm font-mono-label text-[color:hsl(var(--teal))]"><DollarSign className="w-4 h-4" /> Kudos revenue</div>
          <div className="mt-3 space-y-1.5 text-sm">
            <div className="flex justify-between"><span>Gross Kudos</span><b>${data.kudos_pending_gross.toFixed(2)}</b></div>
            <div className="flex justify-between text-muted-foreground"><span>CLING platform fee (blended {(data.kudos_fee_pct*100).toFixed(1)}%)</span>–${data.kudos_fee_total.toFixed(2)}</div>
            <div className="pt-2 border-t flex justify-between font-semibold"><span>Your net</span><span data-testid="kudos-net">${data.kudos_net_pending.toFixed(2)}</span></div>
          </div>
          <div className="mt-3 flex items-start gap-1.5 text-[11px] text-muted-foreground">
            <Info className="w-3 h-3 mt-0.5" />
            Next Kudos will use the current fee of {(data.kudos_current_fee_pct*100).toFixed(1)}%. Past Kudos are pinned at the rate they were sent at.
          </div>
        </div>

        <div className="mt-4 border rounded-2xl bg-card p-5">
          <div className="text-sm font-mono-label text-muted-foreground">Immediate Help sessions</div>
          <div className="mt-2 text-sm text-muted-foreground">Completed paid sessions will show here. Funds arrive in your Stripe balance shortly after each session ends.</div>
        </div>

        <div className="mt-6">
          <Link to="/helper-pro" className="text-sm text-[color:hsl(var(--teal))] underline underline-offset-2">See Helper Pro savings calculator →</Link>
        </div>
      </div>
    </div>
  );
}

function ConnectPayoutsCard({ connect, onStart, starting, onRefresh }) {
  if (!connect) return null;
  const active = connect.charges_enabled && connect.payouts_enabled;
  const started = connect.connected;
  return (
    <div
      className={`mt-6 rounded-2xl border p-5 ${active ? "border-emerald-500/40 bg-emerald-500/5" : "border-[color:hsl(var(--amber))]/40 bg-[color:hsl(var(--amber))]/5"}`}
      data-testid="connect-payouts-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm font-mono-label text-[color:hsl(var(--teal))]">
            <ShieldCheck className="w-4 h-4" />
            Stripe payouts
          </div>
          <div className="mt-1 font-display font-bold text-lg" data-testid="connect-status-label">
            {active ? "You're set up to receive payouts" : started ? "Almost there — finish Stripe verification" : "Get paid by connecting Stripe"}
          </div>
          <p className="mt-1 text-sm text-muted-foreground max-w-xl">
            {active
              ? "Kudos and Immediate Help earnings settle directly to your Stripe balance and pay out to your bank on Stripe's schedule."
              : started
              ? "Stripe still needs a few details before payouts can start. Continue where you left off — it only takes a couple minutes."
              : "Stripe handles payouts safely, no card details ever touch CLING. Set up once, get paid every time."}
          </p>
          {active && connect.balance && (
            <div className="mt-3 grid grid-cols-2 gap-2 max-w-sm text-sm">
              <div className="rounded-xl bg-background/60 border p-2">
                <div className="text-[11px] font-mono-label text-muted-foreground">Available</div>
                <div className="font-display font-bold" data-testid="stripe-available">${connect.balance.available.toFixed(2)}</div>
              </div>
              <div className="rounded-xl bg-background/60 border p-2">
                <div className="text-[11px] font-mono-label text-muted-foreground">Pending</div>
                <div className="font-display font-bold" data-testid="stripe-pending">${connect.balance.pending.toFixed(2)}</div>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {!active && (
            <Button
              onClick={onStart}
              disabled={starting}
              className="btn-amber rounded-full whitespace-nowrap"
              data-testid="connect-stripe-btn"
            >
              {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-1" />}
              {started ? "Continue Stripe" : "Connect Stripe"}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onRefresh} className="rounded-full text-xs" data-testid="refresh-connect-btn">
            Refresh status
          </Button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border rounded-2xl bg-card p-4">
      <div className="text-xs font-mono-label text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display font-extrabold text-2xl ${accent ? "text-[color:hsl(var(--amber))]" : ""}`}>{value}</div>
    </div>
  );
}
